/**
 * @fileoverview Single-thread reader for a job mailbox (MOR-529).
 *
 * Reference-style reading pane: counterparty header (avatar, name, actions),
 * serif subject with contact-role chips, flat message blocks separated by
 * dividers, and an INLINE reply composer pinned under the messages (To /
 * Subject / body / Send) — replies never open a modal. Replies always pass
 * the last message's Inkbox id as `inReplyTo` so the conversation stays on
 * one thread. Clicking a matched contact opens the full contact panel via
 * `onOpenContact`; unmatched addresses fall back to the lightweight popover.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { diffLines } from "diff";
import { useAction, useConvex, useMutation, useQuery } from "convex/react";
import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Check,
  Clock,
  CornerUpLeft,
  CornerUpRight,
  ExternalLink,
  FileText,
  History,
  MailIcon,
  Minimize2,
  Paperclip,
  ReplyAll,
  Send,
  Sparkles,
  Wand2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Spinner } from "@/ui/primitives/spinner";
import { Textarea } from "@/ui/primitives/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { ROLE_META, type ContactRole } from "@/lib/contact-roles";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { avatarTint, initialsFor } from "./avatar";
import {
  AttachmentPreviewModal,
  type AttachmentPreview,
} from "./AttachmentPreviewModal";
import { splitQuotedText } from "./quotedText";
import {
  computeDefaultExpandedIds,
  computeDefaultReplyTo,
  computeHeaderRoles,
  mergeAddresses,
  selectReplySource,
} from "./threadGrouping";
import { ApprovalAuditLog } from "./ApprovalAuditLog";
import { deliveryLabel } from "./deliveryLabel";
import { ComposeDialog, type ComposePrefill } from "./ComposeDialog";
import { RejectDraftDialog, ReviseDraftDialog } from "./DraftActionDialogs";
import { ContactPopoverCard } from "./ContactPopoverCard";
import { RecipientInput } from "./RecipientInput";
import {
  AttachmentChipList,
  MailAttachmentPicker,
} from "./MailAttachmentPicker";
import { useDraftAutosave } from "./useDraftAutosave";
import type {
  ComposerAttachment,
  MailDraft,
  MailMessage,
  ThreadParticipant,
} from "./types";

interface ThreadViewProps {
  jobId: Id<"jobs">;
  threadId: Id<"mailThreads">;
  /**
   * The mailbox's own address — excluded from reply recipients. Optional:
   * derived from the thread payload when omitted (workspace inbox).
   */
  mailboxEmail?: string;
  /** Opens the full contact detail panel for a matched job contact. */
  onOpenContact?: (contactId: Id<"jobContacts">) => void;
}

const DELIVERY_BADGE: Record<
  NonNullable<MailMessage["deliveryStatus"]>,
  { label: string; destructive: boolean }
> = {
  queued: { label: "Queued", destructive: false },
  sent: { label: "Sent", destructive: false },
  delivered: { label: "Delivered", destructive: false },
  bounced: { label: "Bounced", destructive: true },
  failed: { label: "Failed", destructive: true },
  // Read through `deliveryLabel`: the detail says whether it was spam.
  complained: { label: "Complaint", destructive: true },
};

/** Trigger that opens the full contact panel when matched, popover otherwise. */
function ContactName({
  jobId,
  email,
  label,
  jobContactId,
  onOpenContact,
  className,
}: {
  jobId: Id<"jobs">;
  email: string;
  label: string;
  jobContactId: Id<"jobContacts"> | null | undefined;
  onOpenContact?: (contactId: Id<"jobContacts">) => void;
  className?: string;
}) {
  if (jobContactId && onOpenContact) {
    return (
      <button
        type="button"
        onClick={() => onOpenContact(jobContactId)}
        className={cn("hover:underline", className)}
      >
        {label}
      </button>
    );
  }
  return (
    <ContactPopoverCard jobId={jobId} email={email}>
      <button type="button" className={cn("hover:underline", className)}>
        {label}
      </button>
    </ContactPopoverCard>
  );
}

export function ThreadView({
  jobId,
  threadId,
  mailboxEmail,
  onOpenContact,
}: ThreadViewProps) {
  const data = useQuery(api.mail.queries.getThreadWithMessages, {
    threadId,
  });
  const markThreadRead = useMutation(api.mail.mutations.markThreadRead);
  const composeUserEmail = useMutation(api.mail.mutations.composeUserEmail);
  const editDraft = useMutation(api.mail.drafts.editDraft);
  const approveDraft = useMutation(api.mail.drafts.approveDraft);
  const rejectDraft = useMutation(api.mail.drafts.rejectDraft);

  // Morgan's pending reply draft for this thread (if any) rides the composer.
  const pendingDrafts = useQuery(api.mail.drafts.listPendingDraftsForThread, {
    threadId,
  });
  const agentDraft = pendingDrafts?.[0] ?? null;

  // The user's own autosaved reply draft for this thread (MOR-783).
  const userDraft = useQuery(api.mail.userDrafts.getUserDraftForThread, {
    threadId,
  });

  // Morgan's latest decision about this thread — surfaces no-draft calls
  // ("reviewed, chose not to reply") in place, with an override button.
  const threadDecision = useQuery(
    api.jobs.summarization.agents.mailAgent.publicMutations
      .getLatestThreadDecision,
    { threadId },
  ) as
    | { summary: string; decidedAt: number; createdDraft: boolean }
    | null
    | undefined;
  const requestThreadDraft = useMutation(
    api.jobs.summarization.agents.mailAgent.publicMutations.requestThreadDraft,
  );
  const [requestingDraft, setRequestingDraft] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
  const convex = useConvex();
  const [preparingForward, setPreparingForward] = useState(false);
  const [forwardPrefill, setForwardPrefill] = useState<
    ComposePrefill | undefined
  >(undefined);

  // Inline reply composer state (shared between inline and popped-out modes).
  // The composer stays hidden until the reader explicitly replies (or Morgan
  // has a pending draft to review); a bare thread reads like a clean transcript.
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<string[]>([]);
  const [replyCc, setReplyCc] = useState<string[]>([]);
  const [replyBcc, setReplyBcc] = useState<string[]>([]);
  const [replyBody, setReplyBody] = useState("");
  const [replyAttachments, setReplyAttachments] = useState<
    ComposerAttachment[]
  >([]);
  const [sending, setSending] = useState(false);
  const [poppedOut, setPoppedOut] = useState(false);
  const composerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Ask-Morgan-to-revise flow: remember the pre-revision body so the update
  // can render as a diff when the agent's rewrite lands.
  const [reviseOpen, setReviseOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [revising, setRevising] = useState(false);
  const [previousBody, setPreviousBody] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [revertingChanges, setRevertingChanges] = useState(false);
  const requestDraftRevision = useMutation(
    api.jobs.summarization.agents.mailAgent.publicMutations
      .requestDraftRevision,
  );

  // When the agent's revision lands (same draft id, new body), re-seed the
  // composer and flip into diff view.
  useEffect(() => {
    if (!revising || previousBody === null || !agentDraft) return;
    if (agentDraft.bodyText !== previousBody) {
      setRevising(false);
      setShowDiff(true);
      setReplyTo(agentDraft.to);
      setReplyCc(agentDraft.cc);
      setReplyBcc(agentDraft.bcc ?? []);
      setReplyBody(agentDraft.bodyText);
    }
  }, [revising, previousBody, agentDraft]);

  // Fast-revise failure: stop the spinner and surface the error (fixes the
  // old spin-forever behaviour when a revision never landed).
  useEffect(() => {
    if (!revising || !agentDraft) return;
    if (agentDraft.revisionStatus === "failed") {
      setRevising(false);
      setPreviousBody(null);
      toast.error(
        agentDraft.revisionError ??
          "Morgan couldn't revise the draft. Try again.",
      );
    }
  }, [revising, agentDraft]);

  // Mark read exactly once per thread open (guard against the reactive query
  // re-firing after the mutation flips unreadCount).
  const markedRef = useRef<string | null>(null);
  useEffect(() => {
    markedRef.current = null;
  }, [threadId]);
  useEffect(() => {
    if (!data) return;
    if (data.thread.unreadCount > 0 && markedRef.current !== threadId) {
      markedRef.current = threadId;
      void markThreadRead({ threadId });
    }
  }, [data, threadId, markThreadRead]);

  // Superhuman-style stacked transcript: every message collapses to a
  // one-line summary (sender · snippet · time); the latest message is
  // expanded by default. Clicking a collapsed row opens it (and clicking an
  // expanded message's header collapses it again).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Keyed on the last message's id (not the array identity) so reactive
  // refetches don't collapse messages the user has expanded.
  const lastMessageId = data?.messages[data.messages.length - 1]?._id;
  useEffect(() => {
    setExpandedIds(computeDefaultExpandedIds(lastMessageId));
  }, [threadId, lastMessageId]);
  const toggleMessage = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selfAddress = (mailboxEmail ?? data?.mailbox.emailAddress ?? "")
    .trim()
    .toLowerCase();

  // Sender label for the job's own mailbox — the job entity name, e.g.
  // "Greenbuild Pty Ltd (mailbox)". Falls back gracefully until the job
  // name loads.
  const selfLabel = data?.jobName
    ? `${data.jobName} (mailbox)`
    : "This mailbox";

  /** Latest inbound message (preferred reply target), else the last message. */
  const replySource = useMemo(() => {
    if (!data) return null;
    return selectReplySource(data.messages);
  }, [data]);

  const defaultReplyTo = useMemo(
    () => computeDefaultReplyTo(replySource, selfAddress),
    [replySource, selfAddress],
  );

  // Seed the inline composer whenever the thread, its reply target, or
  // Morgan's pending draft changes; keeps user edits once they start typing.
  const seededForRef = useRef<string | null>(null);
  useEffect(() => {
    // Wait for both draft queries before seeding.
    if (pendingDrafts === undefined || userDraft === undefined) return;
    const key = `${threadId}:${replySource?._id ?? "none"}:${agentDraft?._id ?? "user"}:${userDraft?._id ?? "blank"}`;
    if (seededForRef.current === key) return;
    seededForRef.current = key;
    setReviseOpen(false);
    setRejectOpen(false);
    setRevising(false);
    setShowDiff(false);
    setPreviousBody(null);
    setPoppedOut(false);
    if (agentDraft) {
      setReplyTo(agentDraft.to);
      setReplyCc(agentDraft.cc);
      setReplyBcc(agentDraft.bcc ?? []);
      setReplyBody(agentDraft.bodyText);
      setReplyAttachments(
        (agentDraft.attachments ?? []).map((a) => ({
          filename: a.filename,
          contentType: a.contentType,
          storageId: a.storageId,
          documentId: a.documentId,
        })),
      );
    } else if (userDraft) {
      // Resume the user's autosaved reply draft.
      setReplyTo(userDraft.to);
      setReplyCc(userDraft.cc);
      setReplyBcc(userDraft.bcc ?? []);
      setReplyBody(userDraft.bodyText);
      setReplyAttachments(
        (userDraft.attachments ?? []).map((a) => ({
          filename: a.filename,
          contentType: a.contentType,
          storageId: a.storageId,
          documentId: a.documentId,
        })),
      );
    } else {
      setReplyTo(defaultReplyTo);
      setReplyCc([]);
      setReplyBcc([]);
      setReplyBody("");
      setReplyAttachments([]);
    }
    // Auto-open to review Morgan's pending draft or resume the user's own
    // work-in-progress; otherwise wait for a Reply / Reply All click.
    setReplyOpen(Boolean(agentDraft) || Boolean(userDraft));
  }, [
    threadId,
    replySource?._id,
    defaultReplyTo,
    agentDraft,
    pendingDrafts,
    userDraft,
  ]);

  const baseSubject = (data?.thread.subject ?? "").replace(/^\s*re:\s*/i, "");

  // Keep the composer in view whenever it (re)appears inline — opening a
  // thread that has a draft, or bringing a popped-out draft back in — so the
  // user never has to scroll down to find it. Double rAF lets the messages
  // above finish their layout first.
  useEffect(() => {
    if (!replyOpen || poppedOut) return;
    let inner: number | undefined;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        composerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner !== undefined) cancelAnimationFrame(inner);
    };
  }, [replyOpen, poppedOut, threadId]);

  // Gmail-style autosave for plain replies only — agent drafts persist
  // through the audited editDraft flow instead.
  const {
    flush: flushReplyDraft,
    release: releaseReplyDraft,
    discard: discardReplyDraft,
  } = useDraftAutosave({
    jobId,
    enabled: replyOpen && !agentDraft && !sending,
    initialDraftId: userDraft?._id ?? null,
    threadId,
    inReplyToInkboxMessageId: replySource?.inkboxMessageId,
    payload: {
      to: replyTo,
      cc: replyCc,
      bcc: replyBcc,
      subject: `Re: ${baseSubject}`,
      bodyText: replyBody,
      attachments: replyAttachments,
    },
  });

  // A reply whose send was rejected resumes here with its error, so the
  // user can fix it and send again (the row stays Failed until it goes).
  const replyFailedError =
    !agentDraft && userDraft?.status === "failed"
      ? (userDraft.error ?? "Send failed")
      : null;

  const focusComposer = () => {
    // Defer so the composer has mounted (it renders once `replyOpen` flips).
    requestAnimationFrame(() => {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      bodyRef.current?.focus();
    });
  };

  // Reopening a collapsed draft must preserve recipient edits along with
  // its body and attachments; otherwise autosave silently drops Bcc/Cc.
  const hasReplyEdits =
    Boolean(userDraft || agentDraft) ||
    replyBody.length > 0 ||
    replyAttachments.length > 0 ||
    replyTo.join(",") !== defaultReplyTo.join(",") ||
    replyCc.length > 0 ||
    replyBcc.length > 0;

  const handleReply = () => {
    if (replySource && !hasReplyEdits) {
      const sender = replySource.fromAddress.trim();
      setReplyTo(
        sender.toLowerCase() === selfAddress ? defaultReplyTo : [sender],
      );
      setReplyCc([]);
      setReplyBcc([]);
    }
    setReplyOpen(true);
    focusComposer();
  };

  const handleReplyAll = () => {
    const sourceCc = (replySource?.ccAddresses ?? []).filter(
      (a) => a.trim().toLowerCase() !== selfAddress,
    );
    if (hasReplyEdits) {
      // Resuming a draft: widen it to everyone on the original without
      // dropping the user's own recipients (Bcc stays untouched).
      const nextTo = mergeAddresses(replyTo, defaultReplyTo, [
        ...replyCc,
        ...replyBcc,
      ]);
      setReplyTo(nextTo);
      setReplyCc(mergeAddresses(replyCc, sourceCc, [...nextTo, ...replyBcc]));
    } else {
      setReplyTo(defaultReplyTo);
      setReplyCc(sourceCc);
      setReplyBcc([]);
    }
    setReplyOpen(true);
    focusComposer();
  };

  // Collapse the inline composer (only offered for plain replies — an agent
  // draft is dismissed via Reject, not closed). The autosaved draft (if any)
  // stays in the Drafts folder.
  const handleCancelReply = () => {
    setPoppedOut(false);
    setReplyOpen(false);
  };

  // Discard: delete the autosaved draft and close the composer.
  const handleDiscardReply = () => {
    void discardReplyDraft().then(() => {
      setPoppedOut(false);
      setReplyOpen(false);
      setReplyBody("");
      setReplyAttachments([]);
    });
  };

  const handleForward = async () => {
    if (!replySource || preparingForward) return;
    const quoted = [
      "",
      "",
      "---------- Forwarded message ----------",
      `From: ${replySource.fromAddress}`,
      `Subject: ${replySource.subject || "(no subject)"}`,
      "",
      replySource.bodyText,
    ].join("\n");
    setPreparingForward(true);
    try {
      const { attachments, notice } = await convex.query(
        api.mail.forward.getAttachments,
        { messageId: replySource._id },
      );
      setForwardPrefill({
        subject: `Fwd: ${baseSubject}`,
        bodyText: quoted,
        attachments,
      });
      setComposeOpen(true);
      // Never drop an original attachment silently: say what is missing.
      if (notice) toast.warning(notice, { duration: 10_000 });
    } catch (error) {
      toast.error(
        extractErrorMessage(
          error,
          "Could not prepare attachments for forwarding",
        ),
      );
    } finally {
      setPreparingForward(false);
    }
  };

  const handleSendReply = async () => {
    const to = replyTo.map((a) => a.trim()).filter(Boolean);
    const cc = replyCc.map((a) => a.trim()).filter(Boolean);
    const bcc = replyBcc.map((a) => a.trim()).filter(Boolean);
    if (to.length === 0) {
      toast.error("Add at least one recipient.");
      return;
    }
    if (replyBody.trim().length === 0) return;
    setSending(true);
    try {
      if (agentDraft) {
        // Morgan's draft goes through the audited approval flow — persist
        // any edits first, then approve (which schedules the send).
        const attachmentsDirty =
          JSON.stringify(replyAttachments.map((a) => a.storageId)) !==
          JSON.stringify(
            (agentDraft.attachments ?? []).map((a) => a.storageId),
          );
        const dirty =
          to.join(",") !== agentDraft.to.join(",") ||
          cc.join(",") !== agentDraft.cc.join(",") ||
          bcc.join(",") !== (agentDraft.bcc ?? []).join(",") ||
          replyBody !== agentDraft.bodyText ||
          attachmentsDirty;
        if (dirty) {
          await editDraft({
            draftId: agentDraft._id,
            to,
            cc,
            bcc,
            bodyText: replyBody,
            ...(attachmentsDirty
              ? {
                  attachments: replyAttachments.map((a) => ({
                    filename: a.filename,
                    contentType: a.contentType,
                    storageId: a.storageId,
                    documentId: a.documentId,
                  })),
                }
              : {}),
          });
        }
        await approveDraft({ draftId: agentDraft._id });
        toast.success("Approved — sending...");
      } else {
        // Save first so the send adopts the autosaved draft row: a rejected
        // send then resumes here as the same draft (with its error) rather
        // than vanishing.
        const draftId = await flushReplyDraft();
        await composeUserEmail({
          jobId,
          draftId: draftId ?? undefined,
          to,
          cc: cc.length > 0 ? cc : undefined,
          bcc: bcc.length > 0 ? bcc : undefined,
          subject: `Re: ${baseSubject}`,
          bodyText: replyBody,
          // ALWAYS thread the reply onto the last message — outbound or
          // inbound — so the conversation history stays on one thread.
          inReplyToInkboxMessageId: replySource?.inkboxMessageId,
          attachments:
            replyAttachments.length > 0
              ? replyAttachments.map((a) => ({
                  filename: a.filename,
                  contentType: a.contentType,
                  storageId: a.storageId,
                  documentId: a.documentId,
                }))
              : undefined,
        });
        releaseReplyDraft();
        toast.success("Sending...");
        setReplyBody("");
        setReplyBcc([]);
        setReplyAttachments([]);
        setReplyOpen(false);
      }
      setPoppedOut(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to send reply"));
    } finally {
      setSending(false);
    }
  };

  const handleRejectDraft = async (reason: string) => {
    if (!agentDraft) return;
    try {
      await rejectDraft({
        draftId: agentDraft._id,
        reason: reason.trim() || undefined,
      });
      setRejectOpen(false);
      toast.success("Draft rejected");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to reject draft"));
    }
  };

  // Accept Morgan's revision: the revised body is already seeded into the
  // composer, so just dismiss the diff and drop the pre-revision snapshot.
  const handleApproveChanges = () => {
    setShowDiff(false);
    setPreviousBody(null);
    toast.success("Changes applied");
  };

  // Reject Morgan's revision: restore the pre-revision body on both the
  // composer and the persisted draft, then dismiss the diff.
  const handleRejectChanges = async () => {
    if (!agentDraft || previousBody === null) return;
    setRevertingChanges(true);
    try {
      await editDraft({ draftId: agentDraft._id, bodyText: previousBody });
      setReplyBody(previousBody);
      setShowDiff(false);
      setPreviousBody(null);
      toast.success("Changes reverted");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to revert changes"));
    } finally {
      setRevertingChanges(false);
    }
  };

  const handleRequestRevision = async (instructions: string) => {
    if (!agentDraft) return;
    try {
      setPreviousBody(agentDraft.bodyText);
      await requestDraftRevision({
        draftId: agentDraft._id,
        instructions,
      });
      setRevising(true);
      setShowDiff(false);
      setReviseOpen(false);
      toast.success("Morgan is revising the draft...");
    } catch (err) {
      setPreviousBody(null);
      toast.error(extractErrorMessage(err, "Failed to request a revision"));
    }
  };

  if (data === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
        This thread is no longer available.
      </div>
    );
  }

  const participants = data.thread.participants ?? [];
  const headerRoles = computeHeaderRoles(participants);
  const archived = data.thread.folder === "archive";

  return (
    <div className="flex h-full flex-col">
      {/* Header — thread subject + role chips on the left, actions right. */}
      <div className="flex items-center justify-between gap-3 border-b px-6 py-4">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <h2 className="truncate font-[Lora,serif] text-xl font-semibold leading-snug text-foreground">
            {data.thread.subject || "(no subject)"}
          </h2>
          {headerRoles.map((role) => {
            const meta = ROLE_META[role as ContactRole];
            if (!meta) return null;
            return (
              <Badge
                key={role}
                color={meta.badgeColor}
                variant="soft"
                size="sm"
                className="whitespace-nowrap"
              >
                {meta.label}
              </Badge>
            );
          })}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <ThreadActions
            threadId={threadId}
            archived={archived}
            hasInbound={data.thread.hasInbound === true}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Stacked message cards — collapsed to a one-line summary until
            clicked open (latest expanded by default). */}
        <div className="space-y-2 px-6 pb-4 pt-4">
          {data.messages.map((message, index) => (
            <MessageBlock
              key={message._id}
              jobId={jobId}
              message={message}
              participants={participants}
              selfAddress={selfAddress}
              selfLabel={selfLabel}
              onOpenContact={onOpenContact}
              expanded={expandedIds.has(message._id)}
              onToggle={() => toggleMessage(message._id)}
              // Reply actions attach to the foot of the latest message so they
              // read as part of the conversation card (like the draft/new-email
              // composer footers). Hidden once the inline composer is open.
              footer={
                !replyOpen && index === data.messages.length - 1 ? (
                  <>
                    <Button size="sm" variant="outline" onClick={handleReply}>
                      <CornerUpLeft className="mr-1.5 size-3.5" />
                      Reply
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleReplyAll}
                    >
                      <ReplyAll className="mr-1.5 size-3.5" />
                      Reply All
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleForward}
                      disabled={preparingForward}
                    >
                      <CornerUpRight className="mr-1.5 size-3.5" />
                      Forward
                    </Button>
                  </>
                ) : undefined
              }
            />
          ))}
        </div>

        {/* Morgan's no-draft decision — visible exactly where a draft would
            have appeared, with an override. Hidden once a pending draft
            exists (the draft itself is the outcome then) or while a
            requested turn is running. */}
        {!agentDraft &&
          threadDecision &&
          !threadDecision.createdDraft &&
          !revising && (
            <div className="border-t bg-muted/30 px-6 py-3">
              <div className="flex items-start gap-2.5">
                <Sparkles className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground">
                    Morgan reviewed this thread and didn't draft a reply
                    <span className="ml-1.5 font-normal text-muted-foreground">
                      ·{" "}
                      {formatDistanceToNow(threadDecision.decidedAt, {
                        addSuffix: true,
                      })}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    {threadDecision.summary}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={requestingDraft}
                  onClick={() => {
                    setRequestingDraft(true);
                    requestThreadDraft({ threadId })
                      .then(() =>
                        toast.success(
                          "Morgan is drafting a reply — it will appear here shortly.",
                        ),
                      )
                      .catch((err) =>
                        toast.error(
                          extractErrorMessage(err, "Failed to request a draft"),
                        ),
                      )
                      .finally(() => setRequestingDraft(false));
                  }}
                >
                  {requestingDraft ? (
                    <Spinner className="mr-1.5 size-3.5" />
                  ) : (
                    <Wand2 className="mr-1.5 size-3.5" />
                  )}
                  Draft anyway
                </Button>
              </div>
            </div>
          )}

        {/* Inline reply composer — like the reference, no modal. Only shown
            once the reader replies or Morgan has a draft to review. When
            popped out, a slim placeholder holds its place here. */}
        {replyOpen && (
          <div ref={composerRef} className="border-t bg-muted/20 px-6 py-4">
            {poppedOut ? (
              <button
                type="button"
                onClick={() => setPoppedOut(false)}
                className="flex w-full items-center justify-between rounded-xl border bg-white px-4 py-3 text-left text-sm text-muted-foreground shadow-sm transition-colors hover:bg-muted/40 dark:bg-card"
              >
                <span>Draft is popped out — click to bring it back.</span>
                <Minimize2 className="size-3.5" />
              </button>
            ) : (
              <ReplyComposerCard
                jobId={jobId}
                agentDraft={agentDraft}
                subjectLine={
                  agentDraft
                    ? agentDraft.subject
                    : `Re: ${baseSubject || "(no subject)"}`
                }
                contacts={participants}
                replyTo={replyTo}
                replyCc={replyCc}
                replyBcc={replyBcc}
                replyBody={replyBody}
                failedError={replyFailedError}
                composerAttachments={replyAttachments}
                onChangeAttachments={setReplyAttachments}
                sending={sending}
                revising={revising || agentDraft?.revisionStatus === "running"}
                showDiff={showDiff}
                previousBody={previousBody}
                revertingChanges={revertingChanges}
                bodyRef={bodyRef}
                onChangeTo={setReplyTo}
                onChangeCc={setReplyCc}
                onChangeBcc={setReplyBcc}
                onChangeBody={setReplyBody}
                onSend={handleSendReply}
                onRejectDraft={() => setRejectOpen(true)}
                onRequestRevise={() => setReviseOpen(true)}
                onCloseDiff={() => setShowDiff(false)}
                onApproveChanges={handleApproveChanges}
                onRejectChanges={() => void handleRejectChanges()}
                onPopOut={() => setPoppedOut(true)}
                onCancel={agentDraft ? undefined : handleCancelReply}
                onDiscard={agentDraft ? undefined : handleDiscardReply}
              />
            )}
          </div>
        )}
      </div>

      {/* Popped-out composer — Superhuman-style floating draft window,
          anchored bottom-right with a top-left resize grip (dragging grows
          it up/left, which is the only direction that makes sense here). */}
      {poppedOut &&
        createPortal(
          <PopoutWindow>
            <ReplyComposerCard
              jobId={jobId}
              agentDraft={agentDraft}
              subjectLine={
                agentDraft
                  ? agentDraft.subject
                  : `Re: ${baseSubject || "(no subject)"}`
              }
              contacts={participants}
              replyTo={replyTo}
              replyCc={replyCc}
              replyBcc={replyBcc}
              replyBody={replyBody}
              failedError={replyFailedError}
              composerAttachments={replyAttachments}
              onChangeAttachments={setReplyAttachments}
              sending={sending}
              revising={revising || agentDraft?.revisionStatus === "running"}
              showDiff={showDiff}
              previousBody={previousBody}
              revertingChanges={revertingChanges}
              expanded
              onChangeTo={setReplyTo}
              onChangeCc={setReplyCc}
              onChangeBcc={setReplyBcc}
              onChangeBody={setReplyBody}
              onSend={handleSendReply}
              onRejectDraft={() => setRejectOpen(true)}
              onRequestRevise={() => setReviseOpen(true)}
              onCloseDiff={() => setShowDiff(false)}
              onApproveChanges={handleApproveChanges}
              onRejectChanges={() => void handleRejectChanges()}
              onPopIn={() => setPoppedOut(false)}
              onDiscard={agentDraft ? undefined : handleDiscardReply}
            />
          </PopoutWindow>,
          document.body,
        )}

      {/* Forward still uses the compose dialog (free recipients + quoted
          body); replies are inline above. Mounted only while open so each
          forward gets a fresh autosave instance, and closing flushes the
          draft via the hook's unmount save. */}
      {composeOpen && (
        <ComposeDialog
          jobId={jobId}
          open={composeOpen}
          onOpenChange={setComposeOpen}
          prefill={forwardPrefill}
        />
      )}

      <RejectDraftDialog
        open={rejectOpen && agentDraft !== null}
        onOpenChange={setRejectOpen}
        onConfirm={(reason) => void handleRejectDraft(reason)}
      />
      <ReviseDraftDialog
        open={reviseOpen && agentDraft !== null}
        onOpenChange={setReviseOpen}
        onSubmit={(instructions) => void handleRequestRevision(instructions)}
        working={revising}
      />
    </div>
  );
}

/**
 * Floating draft window, anchored bottom-right. The resize grip sits at the
 * TOP-LEFT (CSS `resize` only offers bottom-right, which would drag off
 * screen here): pointer-dragging the grip grows the window up and left.
 */
function PopoutWindow({ children }: { children: React.ReactNode }) {
  const [size, setSize] = useState({ width: 620, height: 560 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);

  const onGripPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height,
    };
  };
  const onGripPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Anchored bottom-right: moving the pointer up/left increases size.
    const width = drag.startWidth + (drag.startX - e.clientX);
    const height = drag.startHeight + (drag.startY - e.clientY);
    setSize({
      width: Math.min(Math.max(width, 420), window.innerWidth - 48),
      height: Math.min(Math.max(height, 320), window.innerHeight * 0.9),
    });
  };
  const onGripPointerUp = () => {
    dragRef.current = null;
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex flex-col drop-shadow-2xl"
      style={{ width: size.width, height: size.height }}
    >
      <button
        type="button"
        aria-label="Resize draft window"
        onPointerDown={onGripPointerDown}
        onPointerMove={onGripPointerMove}
        onPointerUp={onGripPointerUp}
        className="absolute -left-1.5 -top-1.5 z-10 flex size-6 cursor-nwse-resize items-center justify-center rounded-full border bg-white text-muted-foreground shadow-md hover:text-foreground dark:bg-card"
      >
        <svg
          viewBox="0 0 10 10"
          className="size-3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        >
          <path d="M1.5 4.5v-3h3" />
          <path d="M8.5 5.5v3h-3" />
        </svg>
      </button>
      {children}
    </div>
  );
}

/**
 * Composer body that grows with its content (up to a sane cap inline;
 * fills the window when popped out) — an agent draft should be readable
 * without scrolling a squashed 4-row box.
 */
function AutoGrowTextarea({
  bodyRef,
  value,
  onChange,
  expanded,
  hasAgentDraft,
}: {
  bodyRef?: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (v: string) => void;
  expanded?: boolean;
  hasAgentDraft: boolean;
}) {
  const localRef = useRef<HTMLTextAreaElement>(null);
  const ref = bodyRef ?? localRef;

  useEffect(() => {
    const el = ref.current;
    if (!el || expanded) return; // Popped out: flex-fill instead.
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight + 2, 560)}px`;
  }, [value, expanded, ref]);

  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={expanded ? 12 : hasAgentDraft ? 10 : 4}
      placeholder="Write your reply..."
      className={cn(
        "border-0 shadow-none focus-visible:ring-0",
        expanded
          ? "min-h-[200px] flex-1 resize-none"
          : hasAgentDraft
            ? "min-h-[220px] resize-y"
            : "min-h-[96px] resize-y",
      )}
    />
  );
}

/** Line diff between the pre-revision and revised draft bodies. */
function DraftDiffView({
  previousBody,
  currentBody,
  onClose,
  onApprove,
  onReject,
  reverting = false,
}: {
  previousBody: string;
  currentBody: string;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  reverting?: boolean;
}) {
  const parts = useMemo(
    () => diffLines(previousBody, currentBody),
    [previousBody, currentBody],
  );
  return (
    <div className="border-b">
      <div className="flex items-center justify-between gap-2 bg-muted/40 px-4 py-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Morgan's changes
        </span>
        <div className="flex items-center gap-0.5">
          {onReject && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 gap-1 text-xs text-destructive hover:text-destructive"
              onClick={onReject}
              disabled={reverting}
            >
              {reverting ? (
                <Spinner className="size-3" />
              ) : (
                <X className="size-3" />
              )}
              Reject changes
            </Button>
          )}
          {onApprove && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 gap-1 text-xs text-emerald-700 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-400"
              onClick={onApprove}
              disabled={reverting}
            >
              <Check className="size-3" />
              Approve changes
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-xs"
            onClick={onClose}
            disabled={reverting}
          >
            Hide diff
          </Button>
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto px-4 py-2 font-mono text-xs leading-relaxed">
        {parts.map((part, i) => (
          <span
            key={i}
            className={cn(
              "block whitespace-pre-wrap",
              part.added &&
                "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300",
              part.removed &&
                "bg-destructive/10 text-destructive line-through decoration-destructive/40",
            )}
          >
            {part.value}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The reply composer card — rendered inline under the thread or popped out
 * as a floating window (Superhuman-style, resizable). When Morgan has a
 * pending draft for the thread, the card carries it: fields are seeded from
 * the draft, Send runs the audited edit → approve flow, and "Ask Morgan to
 * revise" sends instructions back to the agent — the rewrite lands as a diff.
 */
function ReplyComposerCard({
  jobId,
  agentDraft,
  subjectLine,
  contacts,
  replyTo,
  replyCc,
  replyBcc,
  replyBody,
  failedError,
  composerAttachments,
  onChangeAttachments,
  sending,
  revising,
  showDiff,
  previousBody,
  revertingChanges,
  expanded,
  bodyRef,
  onChangeTo,
  onChangeCc,
  onChangeBcc,
  onChangeBody,
  onSend,
  onRejectDraft,
  onRequestRevise,
  onCloseDiff,
  onApproveChanges,
  onRejectChanges,
  onPopOut,
  onPopIn,
  onCancel,
  onDiscard,
}: {
  jobId: Id<"jobs">;
  agentDraft: MailDraft | null;
  subjectLine: string;
  contacts: ThreadParticipant[];
  replyTo: string[];
  replyCc: string[];
  replyBcc: string[];
  replyBody: string;
  /** Why the previous send of this reply was rejected (resumed failed draft). */
  failedError?: string | null;
  composerAttachments: ComposerAttachment[];
  onChangeAttachments: (next: ComposerAttachment[]) => void;
  sending: boolean;
  revising: boolean;
  showDiff: boolean;
  previousBody: string | null;
  /** True while the pre-revision body is being restored (reject changes). */
  revertingChanges?: boolean;
  expanded?: boolean;
  bodyRef?: React.RefObject<HTMLTextAreaElement>;
  onChangeTo: (v: string[]) => void;
  onChangeCc: (v: string[]) => void;
  onChangeBcc: (v: string[]) => void;
  onChangeBody: (v: string) => void;
  onSend: () => void;
  onRejectDraft: () => void;
  onRequestRevise: () => void;
  onCloseDiff: () => void;
  /** Keep Morgan's revised body and dismiss the diff. */
  onApproveChanges?: () => void;
  /** Revert to the pre-revision body and dismiss the diff. */
  onRejectChanges?: () => void;
  onPopOut?: () => void;
  onPopIn?: () => void;
  /** Close the composer, KEEPING the autosaved draft. */
  onCancel?: () => void;
  /** Delete the autosaved draft and close the composer. */
  onDiscard?: () => void;
}) {
  const [attachmentPreview, setAttachmentPreview] =
    useState<AttachmentPreview | null>(null);
  // Gmail-style: Bcc stays hidden until asked for. Populated values always
  // show — a resumed draft's Bcc is seeded after mount, and recipients that
  // are about to receive the email must never be invisible.
  const [bccOpen, setBccOpen] = useState(false);
  const showBcc = bccOpen || replyBcc.length > 0;
  return (
    <div
      className={cn(
        "flex min-h-0 flex-col overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-card",
        expanded && "h-full",
      )}
    >
      {/* Header: draft provenance + pop-out/in */}
      <div
        className={cn(
          "flex items-center justify-between gap-2 border-b px-4 py-2",
          agentDraft && "bg-primary/5",
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium">
          {agentDraft ? (
            <>
              <Sparkles className="size-3.5 shrink-0 text-primary" />
              <span className="truncate text-primary">
                Morgan drafted this reply — review and send
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">Reply</span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-0.5">
          {onPopOut && (
            <Button
              size="icon"
              variant="ghost"
              className="size-6"
              onClick={onPopOut}
              title="Pop out draft"
              aria-label="Pop out draft"
            >
              <ExternalLink className="size-3.5" />
            </Button>
          )}
          {onPopIn && (
            <Button
              size="icon"
              variant="ghost"
              className="size-6"
              onClick={onPopIn}
              title="Pop draft back in"
              aria-label="Pop draft back in"
            >
              <X className="size-3.5" />
            </Button>
          )}
          {onCancel && (
            <Button
              size="icon"
              variant="ghost"
              className="size-6"
              onClick={onCancel}
              title="Close (draft is kept)"
              aria-label="Close reply"
            >
              <X className="size-3.5" />
            </Button>
          )}
        </span>
      </div>

      {agentDraft?.agentRationale && (
        <p className="border-b bg-primary/5 px-4 py-2 text-xs leading-relaxed text-foreground/70">
          {agentDraft.agentRationale}
        </p>
      )}

      {revising && (
        <div className="flex items-center gap-2 border-b bg-primary/5 px-4 py-2 text-xs text-primary">
          <Spinner className="size-3.5" />
          Morgan is revising this draft...
        </div>
      )}

      {failedError && (
        <div
          role="alert"
          className="flex items-start gap-2 border-b border-destructive/30 bg-destructive/5 px-4 py-2"
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-destructive">
              This reply couldn't be sent — fix it and send again
            </p>
            <p className="mt-0.5 break-words text-xs leading-relaxed text-destructive/80">
              {failedError}
            </p>
          </div>
        </div>
      )}

      {showDiff && previousBody !== null && agentDraft && (
        <DraftDiffView
          previousBody={previousBody}
          currentBody={agentDraft.bodyText}
          onClose={onCloseDiff}
          onApprove={onApproveChanges}
          onReject={onRejectChanges}
          reverting={revertingChanges}
        />
      )}

      <div className="flex items-start gap-2 border-b px-4 py-2">
        <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
          To:
        </span>
        <RecipientInput
          value={replyTo}
          onChange={onChangeTo}
          contacts={contacts}
          placeholder="recipient@example.com"
        />
      </div>
      <div className="flex items-start gap-2 border-b px-4 py-2">
        <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
          Cc:
        </span>
        <RecipientInput
          value={replyCc}
          onChange={onChangeCc}
          contacts={contacts}
          placeholder="Optional"
        />
        {!showBcc && (
          <button
            type="button"
            onClick={() => setBccOpen(true)}
            className="shrink-0 pt-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            Bcc
          </button>
        )}
      </div>
      {showBcc && (
        <div className="flex items-start gap-2 border-b px-4 py-2">
          <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
            Bcc:
          </span>
          <RecipientInput
            value={replyBcc}
            onChange={onChangeBcc}
            contacts={contacts}
            placeholder="Recipients who won't see each other"
          />
        </div>
      )}
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">
          Subject:
        </span>
        <span className="truncate text-sm text-foreground">{subjectLine}</span>
      </div>
      <AutoGrowTextarea
        bodyRef={bodyRef}
        value={replyBody}
        onChange={onChangeBody}
        expanded={expanded}
        hasAgentDraft={Boolean(agentDraft)}
      />

      {/* Generated Day One letters (no PDF) get a document card previewing
          the email HTML. */}
      {agentDraft?.jobPrecedentId && agentDraft.bodyHtml && (
        <div className="flex flex-wrap gap-1.5 border-t px-4 py-2">
          <button
            type="button"
            onClick={() =>
              setAttachmentPreview({
                title: agentDraft.subject || "Generated document",
                html: agentDraft.bodyHtml,
              })
            }
            className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-2.5 py-1 text-xs text-foreground transition-colors hover:border-primary/40 hover:bg-muted"
          >
            <FileText className="size-3 text-primary" />
            Generated document
          </button>
        </div>
      )}

      {/* Attachments on the outgoing email — chips with preview + remove. */}
      <AttachmentChipList
        jobId={jobId}
        attachments={composerAttachments}
        onRemove={(index) =>
          onChangeAttachments(composerAttachments.filter((_, i) => i !== index))
        }
      />

      <AttachmentPreviewModal
        preview={attachmentPreview}
        onClose={() => setAttachmentPreview(null)}
      />

      <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
        <span className="flex items-center gap-1">
          <MailAttachmentPicker
            jobId={jobId}
            attachments={composerAttachments}
            onChange={onChangeAttachments}
            disabled={sending || revising}
          />
          {agentDraft && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={onRejectDraft}
                disabled={sending || revising}
              >
                Reject draft
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onRequestRevise}
                disabled={sending || revising}
              >
                <Wand2 className="mr-1.5 size-3.5" />
                Ask Morgan to revise
              </Button>
            </>
          )}
        </span>
        <span className="flex items-center gap-2">
          {!agentDraft && replyBody.trim().length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDiscard ?? (() => onChangeBody(""))}
              disabled={sending}
            >
              Discard
            </Button>
          )}
          <Button
            size="sm"
            onClick={onSend}
            disabled={sending || revising || replyBody.trim().length === 0}
          >
            {sending ? (
              <Spinner className="mr-1.5 size-3.5" />
            ) : (
              <Send className="mr-1.5 size-3.5" />
            )}
            {agentDraft ? "Approve & send" : "Send"}
          </Button>
        </span>
      </div>
    </div>
  );
}

/** Archive / snooze / unread controls in the reader header. */
function ThreadActions({
  threadId,
  archived,
  hasInbound,
}: {
  threadId: Id<"mailThreads">;
  archived: boolean;
  hasInbound: boolean;
}) {
  const setThreadFolder = useMutation(api.mail.mutations.setThreadFolder);
  const snoozeThread = useMutation(api.mail.mutations.snoozeThread);
  const markThreadUnread = useMutation(api.mail.mutations.markThreadUnread);

  const handleArchive = async () => {
    try {
      await setThreadFolder({
        threadId,
        folder: archived ? "inbox" : "archive",
      });
      toast.success(archived ? "Moved to inbox" : "Archived");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not move the thread"));
    }
  };

  const handleSnooze = async (until: number) => {
    try {
      await snoozeThread({ threadId, until });
      toast.success("Snoozed");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not snooze the thread"));
    }
  };

  const handleMarkUnread = async () => {
    try {
      await markThreadUnread({ threadId });
      toast.success("Marked as unread");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not mark as unread"));
    }
  };

  const now = new Date();
  const laterToday = new Date(now.getTime() + 4 * 60 * 60 * 1000);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  const nextWeek = new Date(now);
  nextWeek.setDate(nextWeek.getDate() + ((8 - nextWeek.getDay()) % 7 || 7));
  nextWeek.setHours(9, 0, 0, 0);

  return (
    <>
      {hasInbound && (
        <Button
          size="icon"
          variant="ghost"
          className="size-8"
          onClick={handleMarkUnread}
          title="Mark as unread"
          aria-label="Mark as unread"
        >
          <MailIcon className="size-4" />
        </Button>
      )}
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        onClick={handleArchive}
        title={archived ? "Move to inbox" : "Archive"}
        aria-label={archived ? "Move to inbox" : "Archive"}
      >
        {archived ? (
          <ArchiveRestore className="size-4" />
        ) : (
          <Archive className="size-4" />
        )}
      </Button>
      {!archived && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-8"
              title="Snooze"
              aria-label="Snooze"
            >
              <Clock className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
              Snooze until
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => void handleSnooze(laterToday.getTime())}
            >
              Later today
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void handleSnooze(tomorrow.getTime())}
            >
              Tomorrow, 9am
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void handleSnooze(nextWeek.getTime())}
            >
              Next week, Mon 9am
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

function MessageBlock({
  jobId,
  message,
  participants,
  selfAddress,
  selfLabel,
  onOpenContact,
  expanded,
  onToggle,
  footer,
}: {
  jobId: Id<"jobs">;
  message: MailMessage;
  participants: ThreadParticipant[];
  selfAddress: string;
  /** Display label for the job's own mailbox (e.g. "Acme Pty Ltd (mailbox)"). */
  selfLabel: string;
  onOpenContact?: (contactId: Id<"jobContacts">) => void;
  /** Whether the message body is shown (vs. a one-line collapsed summary). */
  expanded: boolean;
  /** Toggles the collapsed/expanded state. */
  onToggle: () => void;
  /**
   * Optional action row rendered inside the card, beneath the message, as a
   * bordered footer. Used to attach the thread's Reply / Reply All / Forward
   * actions to the latest message so they read as part of the card.
   */
  footer?: React.ReactNode;
}) {
  const outbound = message.direction === "outbound";
  const [showHtml, setShowHtml] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showQuoted, setShowQuoted] = useState(false);
  // Only surface the Text/HTML toggle on inbound mail: outbound messages get
  // an auto-generated HTML part from Inkbox that just mirrors the plain text,
  // so the toggle would be redundant there.
  const hasHtml = Boolean(message.bodyHtmlUrl) && !outbound;

  // Attachment preview: filed attachments resolve to storage URLs via the
  // query; unfiled ones mint a live Inkbox URL through the action.
  const [attachmentPreview, setAttachmentPreview] =
    useState<AttachmentPreview | null>(null);
  const [loadingAttachment, setLoadingAttachment] = useState<string | null>(
    null,
  );
  const attachmentAccess = useQuery(
    api.mail.queries.getMessageAttachmentUrls,
    message.attachments.length > 0 ? { messageId: message._id } : "skip",
  ) as
    | { files: Array<{ filename: string; url: string | null }> }
    | null
    | undefined;
  const fetchAttachmentUrl = useAction(
    api.integrations.inkbox.actions.getInboundAttachmentUrl,
  );
  const openAttachment = async (filename: string) => {
    const filed = attachmentAccess?.files.find(
      (f) => f.filename === filename,
    )?.url;
    if (filed) {
      setAttachmentPreview({ title: filename, url: filed });
      return;
    }
    setLoadingAttachment(filename);
    try {
      const url = await fetchAttachmentUrl({
        messageId: message._id,
        filename,
      });
      if (url) {
        setAttachmentPreview({ title: filename, url });
      } else {
        toast.error("Could not load this attachment.");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not load this attachment"));
    } finally {
      setLoadingAttachment(null);
    }
  };
  const { main: bodyMain, quoted: bodyQuoted } = useMemo(
    () => splitQuotedText(message.bodyText),
    [message.bodyText],
  );
  // First non-empty line of the body — the collapsed row's preview text.
  const snippet = useMemo(() => {
    const source = bodyMain || message.bodyText || "";
    const firstLine = source
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find(Boolean);
    return (
      firstLine ??
      (message.bodyTruncated ? "Loading full message…" : "(no content)")
    );
  }, [bodyMain, message.bodyText, message.bodyTruncated]);
  const delivery = message.deliveryStatus
    ? DELIVERY_BADGE[message.deliveryStatus]
    : null;

  const fromLower = message.fromAddress.trim().toLowerCase();
  const isSelf = fromLower === selfAddress;
  const contact = participants.find(
    (p) => p.email.trim().toLowerCase() === fromLower,
  );
  const senderName = isSelf
    ? selfLabel
    : (contact?.displayName ?? message.fromAddress);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-card",
        !expanded && "transition-colors hover:bg-muted/30",
      )}
    >
      {!expanded ? (
        /* Collapsed summary — sender · first line · time, one click to open */
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center gap-3 px-5 py-3 text-left"
        >
          <div
            className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
              isSelf
                ? "bg-primary/10 text-primary"
                : avatarTint(message.fromAddress),
            )}
          >
            {initialsFor(senderName)}
          </div>
          <span className="max-w-[40%] shrink-0 truncate text-sm font-semibold text-foreground">
            {senderName}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
            {snippet}
          </span>
          {message.attachments.length > 0 && (
            <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {formatDistanceToNow(message.sentAt, { addSuffix: true })}
          </span>
        </button>
      ) : (
        <div className="p-5">
          {/* Header — clicking anywhere but the interactive bits collapses it */}
          <div
            className="flex cursor-pointer items-start justify-between gap-3"
            onClick={onToggle}
          >
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={cn(
                  "flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                  isSelf
                    ? "bg-primary/10 text-primary"
                    : avatarTint(message.fromAddress),
                )}
              >
                {initialsFor(senderName)}
              </div>
              <div className="min-w-0">
                <span
                  className="block min-w-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ContactName
                    jobId={jobId}
                    email={message.fromAddress}
                    label={senderName}
                    jobContactId={contact?.jobContactId}
                    onOpenContact={onOpenContact}
                    className="block truncate text-sm font-semibold text-foreground"
                  />
                </span>
                <p className="truncate text-xs text-muted-foreground">
                  to {message.toAddresses.join(", ") || "(no recipients)"}
                  {message.ccAddresses.length > 0 &&
                    `, cc ${message.ccAddresses.join(", ")}`}
                  {(message.bccAddresses?.length ?? 0) > 0 &&
                    `, bcc ${(message.bccAddresses ?? []).join(", ")}`}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 pt-0.5">
              {outbound && delivery && (
                <Badge
                  variant={delivery.destructive ? "destructive" : "secondary"}
                  className="text-[10px]"
                >
                  {deliveryLabel(message, delivery.label)}
                </Badge>
              )}
              <span className="text-[11px] text-muted-foreground">
                {formatDistanceToNow(message.sentAt, { addSuffix: true })}
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="size-6 text-muted-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowHistory((v) => !v);
                }}
                title="Message history"
                aria-label="Message history"
              >
                <History className="size-3.5" />
              </Button>
            </div>
          </div>

          {showHistory && (
            <div className="mt-3 rounded-lg border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                History
              </p>
              <p>
                {outbound
                  ? `Sent by ${message.sentByName ?? "Morgan (automated)"} · ${new Date(message.sentAt).toLocaleString()}`
                  : `Received from ${message.fromAddress} · ${new Date(message.sentAt).toLocaleString()}`}
                {outbound && message.deliveryStatus && (
                  <> · delivery: {message.deliveryStatus}</>
                )}
              </p>
              {message.draftId && (
                <div className="mt-2 border-t pt-2">
                  <ApprovalAuditLog draftId={message.draftId} />
                </div>
              )}
            </div>
          )}

          <div className="mt-4 pl-11">
            {hasHtml && (
              <div className="mb-2 flex items-center gap-1 text-xs">
                <button
                  type="button"
                  onClick={() => setShowHtml(false)}
                  className={cn(
                    "rounded px-1.5 py-0.5",
                    !showHtml
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Text
                </button>
                <button
                  type="button"
                  onClick={() => setShowHtml(true)}
                  className={cn(
                    "rounded px-1.5 py-0.5",
                    showHtml
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  HTML
                </button>
              </div>
            )}
            {hasHtml && showHtml ? (
              <iframe
                title="Message body"
                sandbox=""
                src={message.bodyHtmlUrl ?? undefined}
                className="h-96 w-full rounded border bg-white"
              />
            ) : (
              <>
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90">
                  {bodyMain ||
                    (message.bodyText
                      ? message.bodyText
                      : message.bodyTruncated
                        ? "Loading full message..."
                        : "(empty message)")}
                </p>
                {bodyQuoted && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => setShowQuoted((v) => !v)}
                      title={
                        showQuoted ? "Hide quoted text" : "Show quoted text"
                      }
                      aria-label={
                        showQuoted ? "Hide quoted text" : "Show quoted text"
                      }
                      className="rounded-full border bg-muted/60 px-2 py-0.5 text-xs font-bold leading-none tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      •••
                    </button>
                    {showQuoted && (
                      <p className="mt-2 whitespace-pre-wrap break-words border-l-2 border-border pl-3 text-sm leading-relaxed text-muted-foreground">
                        {bodyQuoted}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            {message.bodyTruncated &&
              message.bodyText.length > 0 &&
              !showHtml && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Message truncated.
                </p>
              )}

            {message.attachments.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-muted-foreground">
                  {message.attachments.length} attachment
                  {message.attachments.length === 1 ? "" : "s"}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {message.attachments.map((attachment, i) => (
                    <button
                      key={`${attachment.filename}-${i}`}
                      type="button"
                      disabled={loadingAttachment === attachment.filename}
                      onClick={() => void openAttachment(attachment.filename)}
                      className="flex items-center gap-2.5 rounded-lg border bg-background px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-muted/40 disabled:opacity-60"
                    >
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                        {loadingAttachment === attachment.filename ? (
                          <Spinner className="size-3.5 text-primary" />
                        ) : (
                          <Paperclip className="size-3.5 text-primary" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="max-w-[200px] truncate text-xs font-medium text-foreground">
                          {attachment.filename}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {attachment.size
                            ? attachment.size > 1024 * 1024
                              ? `${(attachment.size / (1024 * 1024)).toFixed(1)} MB · Preview`
                              : `${Math.max(1, Math.round(attachment.size / 1024))} KB · Preview`
                            : attachment.filingStatus === "filed"
                              ? "Filed to documents · Preview"
                              : "Preview"}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {footer && (
        <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-4 py-2.5">
          {footer}
        </div>
      )}

      <AttachmentPreviewModal
        preview={attachmentPreview}
        onClose={() => setAttachmentPreview(null)}
      />
    </div>
  );
}
